import React, { Component } from 'react';
import { observer } from 'mobx-react';
import { reaction } from 'mobx';
import { Flex } from 'core/components';
import { formatDateTime } from 'core/util/dateUtils';
import { ProductSkeleton } from 'core/components/Skeletons';
import UserMessage from 'app/views/core/kaizen/components/UserMessage';
import AgentMessage from 'app/views/core/kaizen/components/AgentMessage';
import ThinkingIndicator from 'app/views/core/kaizen/components/ThinkingIndicator';
import { ChatContainer, MessagesWrapper } from './KaizenChatSession.styles';

@observer
class KaizenChatSession extends Component {
  chatContainerRef = React.createRef();

  latestUserMessageRef = React.createRef();

  state = {
    stepReasoningOpenState: {},
    scrollSpacer: 0
  };

  constructor(props) {
    super(props);

    // Bind methods to avoid creating new functions on each render
    this.handleToggleReasoning = this.handleToggleReasoning.bind(this);
    this.handleSelectToolResult = this.handleSelectToolResult.bind(this);
    this.formatDate = this.formatDate.bind(this);
  }

  componentDidMount() {
    const { chatSessionModel, isExportMode } = this.props;

    // Skip auto-scroll behavior in export mode — no interactive viewport
    if (isExportMode) {
      return;
    }

    // Initial scroll for existing messages when component mounts
    if ((chatSessionModel?.get('messages') || []).length > 0) {
      this.applyNewMessageScroll();
    }

    this.messageReactionDisposer = reaction(
      // Directly watch changes to messages.
      () => chatSessionModel?.get('messages')?.length || 0,
      () => {
        this.applyNewMessageScroll();
      }
    );
  }

  componentWillUnmount() {
    const { chatSessionModel } = this.props;
    if (chatSessionModel) {
      chatSessionModel.onAgentStep = undefined;
      chatSessionModel.onToolResult = undefined;
    }
    if (this.messageReactionDisposer) {
      this.messageReactionDisposer();
    }
  }

  applyNewMessageScroll = () => {
    if (!this.chatContainerRef?.current) {
      return; // Exit if the ref is not yet set
    }

    try {
      const scrollContainer = this.chatContainerRef.current.parentElement;
      if (scrollContainer) {
        const containerHeight = scrollContainer.clientHeight;
        // Define the desired offset from the top of the scroll container for the last message
        const desiredTopOffset = 20;

        // Calculate the scroll spacer height. This spacer ensures that the last message
        // can be scrolled up to the desiredTopOffset from the top of the container.
        // The spacer needs to fill the remaining height of the container below the desired offset.
        const spacerHeight = Math.max(0, containerHeight - desiredTopOffset);

        this.setState({ scrollSpacer: spacerHeight }, () => {
          requestAnimationFrame(() => {
            // Ensure chatContainerRef.current is still valid inside the callback
            // as the component might unmount between setState and callback execution.
            if (!this.chatContainerRef?.current) {
              return;
            }
            const messageElements = this.chatContainerRef.current.querySelectorAll('[data-message-index]');
            const lastMessageElement = messageElements[messageElements.length - 1];
            if (lastMessageElement) {
              const messageOffsetTop = lastMessageElement.offsetTop;
              // The scroll target is the top of the last message minus the desired offset
              const scrollTarget = messageOffsetTop - desiredTopOffset;
              scrollContainer.scrollTo({ top: scrollTarget, behavior: 'smooth' });
            }
          });
        });
      }
    } catch (error) {
      // Log the error to help with debugging.
      // eslint-disable-next-line no-console
      console.error('Error during applyNewMessageScroll:', error);
      // Reset scrollSpacer to prevent layout issues
      this.setState({ scrollSpacer: 0 });
    }
  };

  handleSelectToolResult = (tool) => {
    const { onSelectToolResult } = this.props;
    if (onSelectToolResult) {
      onSelectToolResult(tool);
    }
  };

  handleToggleReasoning = (stepId) => {
    this.setState((prevState) => ({
      stepReasoningOpenState: {
        ...prevState.stepReasoningOpenState,
        [stepId]: !(prevState.stepReasoningOpenState[stepId] ?? true)
      }
    }));
  };

  handleShowAgentDataInSidebar = (turn, steps) => {
    // eslint-disable-next-line no-console
    console.table(turn);
    // eslint-disable-next-line no-console
    console.table(steps);
  };

  formatDate = (dateTime) => formatDateTime(new Date(dateTime), 'MMM DD, YYYY, hh:mm a');

  renderMessage(message) {
    const { stepReasoningOpenState } = this.state;
    const { chatSessionModel, isExistingChatLoading, isCopilotMode, isExportMode, onSendToExplorer } = this.props;
    const messages = chatSessionModel?.get('messages') || [];
    const messageIndex = messages.indexOf(message);
    const isLatestUserMessage = messageIndex === messages.length - 1 && message.message_text;

    const userMessage = <UserMessage message={message} formatDate={this.formatDate} />;

    const agentMessages = message.agentTurn?.map((turn) => (
      <AgentMessage
        key={`agent-${turn.id}`}
        turn={turn}
        stepReasoningOpenState={stepReasoningOpenState}
        onToggleReasoning={this.handleToggleReasoning}
        onSelectToolResult={this.handleSelectToolResult}
        onSendToExplorer={onSendToExplorer}
        onShowAgentDataInSidebar={this.handleShowAgentDataInSidebar}
        formatDate={this.formatDate}
        isCopilotMode={isCopilotMode}
        isExistingChatLoading={isExistingChatLoading}
        sessionModel={chatSessionModel}
      />
    ));

    return (
      <MessagesWrapper
        key={message.id}
        ref={isLatestUserMessage ? this.latestUserMessageRef : null}
        data-message-index={messageIndex}
        isExportMode={isExportMode}
      >
        {userMessage}
        {agentMessages}
      </MessagesWrapper>
    );
  }

  hasToolAwaitingApproval() {
    const { chatSessionModel } = this.props;
    return chatSessionModel?.hasToolAwaitingApproval ?? false;
  }

  render() {
    const { chatSessionModel, isCopilotMode, isExistingChatLoading, isExportMode } = this.props;
    const { scrollSpacer } = this.state;
    const messages = chatSessionModel?.get('messages') || [];
    const isThinking = chatSessionModel?.get('isThinking');
    const hasToolAwaitingApproval = this.hasToolAwaitingApproval();
    const shouldShowThinking = isThinking && !hasToolAwaitingApproval;

    return (
      <ChatContainer ref={this.chatContainerRef} isCopilotMode={isCopilotMode} isExportMode={isExportMode}>
        {messages.length === 0 && isExistingChatLoading && (
          <Flex flexDirection="column" alignItems="center" justifyContent="center" gap="16px" height="100%" flex={1}>
            <ProductSkeleton width="30%" height="16px" style={{ alignSelf: 'flex-end' }} />
            <ProductSkeleton width="75%" height="30px" style={{ alignSelf: 'flex-end' }} />
            <Flex height={50} />
            <ProductSkeleton width="30%" height="16px" style={{ alignSelf: 'flex-start' }} />
            <ProductSkeleton width="75%" height="30px" style={{ alignSelf: 'flex-start' }} />
            <ProductSkeleton width="75%" height="30px" style={{ alignSelf: 'flex-start' }} />
          </Flex>
        )}

        {messages.map((message) => this.renderMessage(message))}
        {shouldShowThinking && <ThinkingIndicator />}

        {/* Invisible spacer to allow scrolling last message to top — skip in export mode */}
        {!isExportMode && scrollSpacer > 0 && <div style={{ height: `${scrollSpacer}px`, flexShrink: 0 }} />}
      </ChatContainer>
    );
  }
}

export default KaizenChatSession;
